import ArrowForwardRounded from '@mui/icons-material/ArrowForwardRounded';
import DevicesRounded from '@mui/icons-material/DevicesRounded';
import DnsRounded from '@mui/icons-material/DnsRounded';
import EventAvailableRounded from '@mui/icons-material/EventAvailableRounded';
import GroupRounded from '@mui/icons-material/GroupRounded';
import HistoryRounded from '@mui/icons-material/HistoryRounded';
import PersonAddAlt1Rounded from '@mui/icons-material/PersonAddAlt1Rounded';
import RefreshRounded from '@mui/icons-material/RefreshRounded';
import {
  Box,
  Button,
  ButtonBase,
  Card,
  IconButton,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { BarChart } from '@mui/x-charts/BarChart';
import { useQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { api, unwrap } from '../../api/client';
import type { PlatformAuditEntry, PlatformDashboard } from '../../api/m19';
import { useAuth } from '../../auth/AuthProvider';
import { EmptyState, ErrorState, LoadingRows, PageHeader } from '../../components/states';
import { StatusChip } from '../../components/StatusChip';
import { Panel, StatGrid, StatTile } from '../../components/ui';
import { hasScreen } from '../index';
import {
  ConversionPanel,
  GrowthPanel,
  PlanMixPanel,
  RenewalsPanel,
  ShopSizePanel,
} from './TrendPanels';
import { count, date, dateTime, plural, relativeTime } from '../../lib/format';
import {
  auditLabel,
  BUCKETS,
  bucketShares,
  DASHBOARD_KEY,
  expiryLabel,
  greeting,
  redemptionRate,
  shortDate,
} from './lib';

/** Platform overview: tenant mix, sign-ups, expiring subscriptions, codes, activity. */
export function DashboardPage() {
  const { me } = useAuth();
  const query = useQuery({
    queryKey: DASHBOARD_KEY,
    queryFn: () => unwrap(api.GET('/platform/dashboard')),
    refetchInterval: 120_000,
  });
  const d = query.data;
  const firstName = me?.name.split(/\s+/)[0] ?? '';

  return (
    <>
      <PageHeader
        eyebrow="Platform overview"
        title={`${greeting()}${firstName ? `, ${firstName}` : ''}`}
        subtitle={new Intl.DateTimeFormat('en-GB', {
          weekday: 'long',
          day: 'numeric',
          month: 'long',
          year: 'numeric',
        }).format(new Date())}
        actions={
          <Tooltip title="Refresh figures">
            <IconButton
              aria-label="Refresh figures"
              onClick={() => void query.refetch()}
              disabled={query.isFetching}
              sx={{ border: 1, borderColor: 'divider', bgcolor: 'background.paper' }}
            >
              <RefreshRounded fontSize="small" />
            </IconButton>
          </Tooltip>
        }
      />

      {query.isError ? (
        <Card>
          <ErrorState error={query.error} onRetry={() => void query.refetch()} />
        </Card>
      ) : (
        <>
          <StatGrid min={210}>
            <StatTile
              label="Tenants on the platform"
              value={d ? count(d.tenants.total) : ''}
              hint={d ? `${plural(d.newTenants30d, 'new shop')} in 30 days` : undefined}
              icon={<DnsRounded />}
              loading={query.isPending}
            />
            <StatTile
              label="New in 30 days"
              value={d ? count(d.newTenants30d) : ''}
              hint={d ? signupTrend(d) : undefined}
              icon={<PersonAddAlt1Rounded />}
              tone="info"
              loading={query.isPending}
            />
            <StatTile
              label="Active shop users"
              value={d ? count(d.activeUsers7d) : ''}
              hint="Signed in during the last 7 days"
              icon={<GroupRounded />}
              tone="success"
              loading={query.isPending}
            />
            <StatTile
              label="Active devices"
              value={d ? count(d.activeDevices7d) : ''}
              hint="Synced during the last 7 days"
              icon={<DevicesRounded />}
              tone="neutral"
              loading={query.isPending}
            />
          </StatGrid>

          <TenantMix data={d} loading={query.isPending} />

          <Box
            sx={{
              display: 'grid',
              gap: 2.5,
              mb: 2.5,
              gridTemplateColumns: { xs: 'minmax(0,1fr)', lg: 'minmax(0,2fr) minmax(0,1fr)' },
            }}
          >
            <SignupsPanel data={d} loading={query.isPending} />
            <CodesPanel data={d} loading={query.isPending} />
          </Box>

          <Box
            sx={{
              display: 'grid',
              gap: 2.5,
              mb: 2.5,
              gridTemplateColumns: { xs: 'minmax(0,1fr)', lg: 'repeat(2, minmax(0,1fr))' },
            }}
          >
            <GrowthPanel data={d} loading={query.isPending} />
            <ConversionPanel data={d} loading={query.isPending} />
          </Box>

          <Box
            sx={{
              display: 'grid',
              gap: 2.5,
              mb: 2.5,
              gridTemplateColumns: { xs: 'minmax(0,1fr)', lg: 'repeat(3, minmax(0,1fr))' },
            }}
          >
            <PlanMixPanel data={d} loading={query.isPending} />
            <RenewalsPanel data={d} loading={query.isPending} />
            <ShopSizePanel data={d} loading={query.isPending} />
          </Box>

          <Box
            sx={{
              display: 'grid',
              gap: 2.5,
              gridTemplateColumns: { xs: 'minmax(0,1fr)', lg: 'minmax(0,1fr) minmax(0,1fr)' },
            }}
          >
            <ExpiringPanel data={d} loading={query.isPending} />
            <ActivityPanel data={d} loading={query.isPending} />
          </Box>
        </>
      )}
    </>
  );
}

function signupTrend(d: PlatformDashboard): string {
  const days = d.signupsByDay;
  const last7 = days.slice(-7).reduce((s, x) => s + x.count, 0);
  return `${plural(last7, 'sign-up')} in the last 7 days`;
}

function TenantMix({ data, loading }: { data?: PlatformDashboard; loading: boolean }) {
  const shares = data ? bucketShares(data.tenants) : null;
  return (
    <Panel title="Tenant status" subtitle="Every shop sits in exactly one bucket" sx={{ mb: 2.5 }}>
      {loading || !data || !shares ? (
        <Skeleton variant="rounded" height={64} />
      ) : (
        <>
          <Box
            role="img"
            aria-label={BUCKETS.map((b) => `${b.label} ${String(data.tenants[b.key])}`).join(', ')}
            sx={{
              display: 'flex',
              gap: '2px',
              height: 12,
              borderRadius: 999,
              overflow: 'hidden',
              bgcolor: 'action.hover',
              mb: 2.25,
            }}
          >
            {BUCKETS.filter((b) => data.tenants[b.key] > 0).map((b) => (
              <Tooltip key={b.key} title={`${b.label}: ${count(data.tenants[b.key])}`}>
                <Box sx={{ width: `${String(shares[b.key])}%`, minWidth: 6, bgcolor: b.color }} />
              </Tooltip>
            ))}
          </Box>
          <Box
            sx={{
              display: 'grid',
              gap: 1.5,
              gridTemplateColumns: {
                xs: 'repeat(2, 1fr)',
                sm: 'repeat(3, 1fr)',
                md: 'repeat(5, 1fr)',
              },
            }}
          >
            {BUCKETS.map((b) => (
              <Box key={b.key} data-testid={`bucket-${b.key}`}>
                <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center' }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '3px', bgcolor: b.color }} />
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 550 }}>
                    {b.label}
                  </Typography>
                </Stack>
                <Typography variant="h5" component="div" className="tabular" sx={{ mt: 0.25 }}>
                  {count(data.tenants[b.key])}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {b.hint} · {Math.round(shares[b.key])}%
                </Typography>
              </Box>
            ))}
          </Box>
        </>
      )}
    </Panel>
  );
}

function SignupsPanel({ data, loading }: { data?: PlatformDashboard; loading: boolean }) {
  const days = data?.signupsByDay ?? [];
  const total = days.reduce((s, x) => s + x.count, 0);
  return (
    <Panel
      title="Sign-ups"
      subtitle={data ? `${plural(total, 'new shop')} in the last 30 days` : 'Last 30 days'}
    >
      {loading ? (
        <Skeleton variant="rounded" height={260} />
      ) : (
        <Box sx={{ mx: -1 }} data-testid="signups-chart">
          <BarChart
            height={260}
            margin={{ left: 0, right: 12, top: 12, bottom: 0 }}
            grid={{ horizontal: true }}
            borderRadius={4}
            hideLegend
            xAxis={[
              {
                scaleType: 'band',
                data: days.map((x) => x.date),
                valueFormatter: (v: string) => shortDate(v),
                tickInterval: (_v: string, i: number) => (days.length - 1 - i) % 7 === 0,
                categoryGapRatio: 0.3,
                tickLabelStyle: { fontSize: 11 },
                disableLine: true,
                disableTicks: true,
              },
            ]}
            yAxis={[
              {
                width: 36,
                tickMinStep: 1,
                valueFormatter: (v: number) => count(v),
                tickLabelStyle: { fontSize: 11 },
                disableLine: true,
                disableTicks: true,
              },
            ]}
            series={[
              {
                id: 'signups',
                label: 'Sign-ups',
                data: days.map((x) => x.count),
                color: 'var(--mui-palette-primary-main)',
                valueFormatter: (v: number | null) => plural(v ?? 0, 'new shop'),
              },
            ]}
            sx={{
              '& .MuiChartsGrid-line': { strokeDasharray: '4 4', opacity: 0.5 },
              '& .MuiChartsAxis-tickLabel': { fill: 'var(--mui-palette-text-secondary)' },
            }}
          />
        </Box>
      )}
    </Panel>
  );
}

function CodesPanel({ data, loading }: { data?: PlatformDashboard; loading: boolean }) {
  const navigate = useNavigate();
  const { can } = useAuth();
  const c = data?.codes30d;
  const rate = c ? redemptionRate(c) : null;
  const rows = c
    ? [
        { label: 'Issued', value: c.issued, color: 'var(--mui-palette-primary-main)' },
        { label: 'Redeemed', value: c.redeemed, color: 'var(--mui-palette-success-main)' },
        { label: 'Revoked', value: c.revoked, color: 'var(--mui-palette-error-main)' },
      ]
    : [];
  const max = Math.max(1, ...rows.map((r) => r.value));
  const link = can('codes.manage') && hasScreen('/activation-codes');
  return (
    <Panel
      title="Activation codes"
      subtitle="Last 30 days"
      action={
        link && (
          <Button
            size="small"
            endIcon={<ArrowForwardRounded />}
            onClick={() => void navigate('/activation-codes')}
          >
            Open
          </Button>
        )
      }
    >
      {loading || !c ? (
        <LoadingRows rows={3} height={36} />
      ) : (
        <Stack spacing={2}>
          <Box>
            <Typography variant="h3" component="div" className="tabular">
              {rate === null ? '—' : `${String(rate)}%`}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {rate === null ? 'No codes issued in 30 days' : 'of issued codes were redeemed'}
            </Typography>
          </Box>
          {rows.map((r) => (
            <Box key={r.label}>
              <Stack direction="row" sx={{ justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 550 }}>
                  {r.label}
                </Typography>
                <Typography variant="body2" className="tabular" sx={{ fontWeight: 650 }}>
                  {count(r.value)}
                </Typography>
              </Stack>
              <Box
                sx={{ height: 6, borderRadius: 999, bgcolor: 'action.hover', overflow: 'hidden' }}
              >
                <Box
                  sx={{
                    height: '100%',
                    width: `${String((r.value / max) * 100)}%`,
                    borderRadius: 999,
                    bgcolor: r.color,
                  }}
                />
              </Box>
            </Box>
          ))}
        </Stack>
      )}
    </Panel>
  );
}

function ListRow({ onClick, children }: { onClick?: () => void; children: ReactNode }) {
  const sx = {
    display: 'flex',
    alignItems: 'center',
    gap: 1.5,
    width: '100%',
    textAlign: 'left',
    px: 2.5,
    py: 1.5,
    borderTop: 1,
    borderColor: 'divider',
  } as const;
  return onClick ? (
    <ButtonBase onClick={onClick} sx={{ ...sx, '&:hover': { bgcolor: 'action.hover' } }}>
      {children}
    </ButtonBase>
  ) : (
    <Box sx={sx}>{children}</Box>
  );
}

function ExpiringPanel({ data, loading }: { data?: PlatformDashboard; loading: boolean }) {
  const navigate = useNavigate();
  const canOpen = hasScreen('/tenants');
  const items = data?.expiringSoon ?? [];
  return (
    <Panel title="Expiring soon" subtitle="Paid periods ending in the next 14 days" padded={false}>
      {loading ? (
        <Box sx={{ p: 2.5 }}>
          <LoadingRows rows={4} />
        </Box>
      ) : items.length === 0 ? (
        <EmptyState
          dense
          icon={<EventAvailableRounded />}
          title="Nothing expiring"
          body="No paid period ends in the next 14 days."
        />
      ) : (
        <Box>
          {items.map((t) => (
            <ListRow
              key={t.tenantId}
              onClick={canOpen ? () => void navigate(`/tenants/${t.tenantId}`) : undefined}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 650 }} noWrap>
                  {t.name}
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap component="div">
                  {t.slug} · paid through {date(t.paidThrough)}
                </Typography>
              </Box>
              <Stack spacing={0.5} sx={{ alignItems: 'flex-end', flexShrink: 0 }}>
                <StatusChip status={t.status} />
                <Typography variant="caption" color="text.secondary" className="tabular">
                  {expiryLabel(t.paidThrough)}
                </Typography>
              </Stack>
            </ListRow>
          ))}
        </Box>
      )}
    </Panel>
  );
}

function ActivityPanel({ data, loading }: { data?: PlatformDashboard; loading: boolean }) {
  const navigate = useNavigate();
  const { can } = useAuth();
  const items = data?.recentAudit ?? [];
  const link = can('audit.view') && hasScreen('/audit');
  return (
    <Panel
      title="Recent activity"
      subtitle="Latest entries in the platform audit log"
      padded={false}
      action={
        link && (
          <Button
            size="small"
            endIcon={<ArrowForwardRounded />}
            onClick={() => void navigate('/audit')}
          >
            View all
          </Button>
        )
      }
    >
      {loading ? (
        <Box sx={{ p: 2.5 }}>
          <LoadingRows rows={4} />
        </Box>
      ) : items.length === 0 ? (
        <EmptyState
          dense
          icon={<HistoryRounded />}
          title="No activity yet"
          body="Platform actions will appear here."
        />
      ) : (
        <Box>
          {items.map((e) => (
            <ActivityRow key={e.id} entry={e} />
          ))}
        </Box>
      )}
    </Panel>
  );
}

function ActivityRow({ entry }: { entry: PlatformAuditEntry }) {
  const tone =
    entry.outcome === 'SUCCESS'
      ? 'var(--mui-palette-success-main)'
      : entry.outcome === 'DENIED'
        ? 'var(--mui-palette-warning-main)'
        : 'var(--mui-palette-error-main)';
  return (
    <ListRow>
      <Box
        sx={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          flexShrink: 0,
          bgcolor: tone,
          boxShadow: `0 0 0 3px color-mix(in srgb, ${tone} 18%, transparent)`,
        }}
      />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
          {auditLabel(entry.action)}
        </Typography>
        <Typography variant="caption" color="text.secondary" noWrap component="div">
          {entry.actorType === 'ANONYMOUS' ? 'Anonymous' : 'Admin'}
          {entry.entityType ? ` · ${entry.entityType.replace(/_/g, ' ')}` : ''}
          {entry.failureReason ? ` · ${entry.failureReason}` : ''}
        </Typography>
      </Box>
      <Stack spacing={0.5} sx={{ alignItems: 'flex-end', flexShrink: 0 }}>
        {entry.outcome !== 'SUCCESS' && <StatusChip status={entry.outcome} />}
        <Typography variant="caption" color="text.secondary" title={dateTime(entry.occurredAt)}>
          {relativeTime(entry.occurredAt)}
        </Typography>
      </Stack>
    </ListRow>
  );
}
